import { useMemo, useCallback } from 'react'
import useCharterDateRangesByMonth from 'hooks/CharterFlights/useCharterDateRangesByMonth'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { UseCharterBundleFallbackPricingParams, CharterBundleFallbackPricingResult, CalendarItemBundleData } from './types'
import { useOfferCharterCalendarData } from './useOfferCharterCalendarData'
import { MALDIVES_BUNDLE_DEFAULT_NIGHTS } from 'constants/charterFlight'

/**
 * Hook to get fallback pricing for charter bundle offers when flight prices are not available
 * Returns the cheapest available bundle pricing from calendar data
 */
export function useCharterBundleFallbackPricing(params: UseCharterBundleFallbackPricingParams): CharterBundleFallbackPricingResult {
  const {
    offer,
    rooms,
    charterBundleRoute,
    fareClass,
    disabled = false,
    multipleRoomBookingActive = false,
    packageUniqueKey,
  } = params

  // Use default occupancy if rooms is empty to ensure consistent pricing
  const effectiveRoomsForCalendar = useMemo(() => {
    const defaultRoomsForCalendar: Array<App.Occupants> = [{ adults: 2, children: 0, infants: 0, teenagers: 0, childrenAge: [] }]
    return rooms && rooms.length > 0 ? rooms : defaultRoomsForCalendar
  }, [rooms])

  // Get dateRangesByMonth for charter calendar
  const { dateRangesByMonth: allDateRangesByMonth } = useCharterDateRangesByMonth({
    charterBundleRoute,
    occupancies: effectiveRoomsForCalendar,
    fareClass,
    disabled: disabled || !charterBundleRoute || effectiveRoomsForCalendar.length === 0,
  })

  // Get charter bundle calendar data
  const { itemBundleDataByDate } = useOfferCharterCalendarData({
    offer,
    nights: MALDIVES_BUNDLE_DEFAULT_NIGHTS,
    rooms: effectiveRoomsForCalendar,
    fareClass,
    dateRangesByMonth: disabled ? {} : allDateRangesByMonth,
    charterBundleRoute,
    multipleRoomBookingActive,
    packageUniqueKey,
  })

  // Find the cheapest bundle entry with its date
  const { cheapestBundleEntry, cheapestDate } = useMemo(() => {
    if (!charterBundleRoute || Object.keys(itemBundleDataByDate).length === 0) {
      return { cheapestBundleEntry: undefined, cheapestDate: undefined }
    }
    const availableEntries = Object.entries(itemBundleDataByDate).filter(
      ([, entry]) => entry.available && entry.totalPrice > 0
    )
    if (availableEntries.length === 0) {
      return { cheapestBundleEntry: undefined, cheapestDate: undefined }
    }
    const [date, entry] = availableEntries.reduce((cheapest, current) =>
      current[1].totalPrice < cheapest[1].totalPrice ? current : cheapest
    )
    return { cheapestBundleEntry: entry, cheapestDate: date }
  }, [charterBundleRoute, itemBundleDataByDate])

  // Find the corresponding date range for the cheapest date (to get checkIn/checkOut and departDate/returnDate)
  const cheapestDateRange = useMemo(() => {
    if (!cheapestDate) return undefined
    for (const ranges of Object.values(allDateRangesByMonth)) {
      const found = ranges.find(range => range.checkIn === cheapestDate)
      if (found) return found
    }
    return undefined
  }, [cheapestDate, allDateRangesByMonth])

  // Build PricingWithValue from bundle data
  // Always return totalPrice, let convertToDisplay handle perPerson conversion
  const buildPricingFromBundle = useCallback((bundleData: CalendarItemBundleData): App.PricingWithValue => {
    const hotelValue = bundleData.hotelValue !== undefined ? bundleData.hotelValue : (bundleData.hotelPrice ?? 0)
    const flightPrice = bundleData.flightPrice ?? 0
    const totalPrice = bundleData.totalPrice
    const totalValue = hotelValue + flightPrice // Value = original hotel value + flight price

    const discountPercent = getValueOffPercent(totalValue, totalPrice)

    return {
      price: totalPrice,
      value: totalValue,
      discountPercent,
      fees: [], // Bundle data doesn't include fees breakdown
      saleUnit: offer.saleUnit,
    }
  }, [offer.saleUnit])

  // Build fallback pricing
  const fallbackPricing = useMemo(() => {
    if (!cheapestBundleEntry) {
      return undefined
    }
    return buildPricingFromBundle(cheapestBundleEntry)
  }, [cheapestBundleEntry, buildPricingFromBundle])

  // Build fallback member pricing
  // Always return totalPrice, let convertToDisplay handle perPerson conversion
  const fallbackMemberPricing = useMemo(() => {
    if (!cheapestBundleEntry?.memberTotalPrice || !fallbackPricing) {
      return undefined
    }
    const memberTotalPrice = cheapestBundleEntry.memberTotalPrice
    const memberValue = fallbackPricing.value // Use same value calculation

    return {
      ...fallbackPricing,
      price: memberTotalPrice,
      value: memberValue,
      discountPercent: getValueOffPercent(memberValue, memberTotalPrice),
    }
  }, [cheapestBundleEntry, fallbackPricing])

  return {
    fallbackPricing,
    fallbackMemberPricing,
    cheapestDateRange,
  }
}
